import dayjs from "../../../utils/dayjs";
import { useMemo } from "react"
import * as Yup from 'yup';
import { MpowerValidationType } from "../../investorOverview/types";
import { SipValidationType } from "../types";
import { InvestmentFundType } from "../../../redux-store/types/api-types";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { handlePickDate } from "./Swp";
import { handleDateValidation } from "./Sip";
import { handleStpDateValidation } from "./Stp";
import { addWorkingDays } from "../../../utils/utilityFunctions";

export const SipScheme = ({ scheme, sipAmount, frequency, noOfInstallments, sipDate, activeCategory, mandate }: {
    scheme: InvestmentFundType,
    sipAmount: any,
    frequency: any,
    noOfInstallments: any,
    sipDate: any,
    endDate: any;
    activeCategory: any,
    mandate: any
}) => {

    return useMemo(() => {

        const DAILY = scheme.validations?.filter((item) => item?.frequency === 'DAILY' && item?.transactionType === 'SIP')?.[0]
        const Weekly = scheme.validations?.filter((item) => item?.frequency === 'WEEKLY' && item?.transactionType === 'SIP')?.[0]
        const WeeklyDays = scheme.validations?.filter((item) => item?.frequency === 'WEEKLYDAYS' && item?.transactionType === 'SIP')?.[0]
        const MONTHLY = scheme.validations?.filter((item) => item?.frequency === 'MONTHLY' && item?.transactionType === 'SIP')?.[0]
        const QUARTERLY = scheme.validations?.filter((item) => item?.frequency === 'QUARTERLY' && item?.transactionType === 'SIP')?.[0]
        const YEARLY = scheme.validations?.filter((item) => item?.frequency === 'YEARLY' && item?.transactionType === 'SIP')?.[0]

        const getDateValidation = (type: "SUB" | "RED" | "SIP" | "STP" | "SWP", period: string): MpowerValidationType => {
            const validationData = scheme?.validations?.filter((item: MpowerValidationType) => item?.transactionType === type && item.frequency === period)[0] ?? {}
            return validationData
        }


        return Yup.object().shape({
            frequency: Yup.string().required('Frequency is required'),
            sipAmount: Yup.number().when('frequency', {
                is: 'DAILY',
                then: Yup.number().min(DAILY?.minimumInstallmentAmount, 'Minimum amoun is ' + DAILY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'WEEKLY',
                then: Yup.number().min(Weekly?.minimumInstallmentAmount, 'Minimum amount is ' + Weekly?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'MONTHLY',
                then: Yup.number().min(MONTHLY?.minimumInstallmentAmount, 'Minimum amount is ' + MONTHLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'QUARTERLY',
                then: Yup.number().min(QUARTERLY?.minimumInstallmentAmount, 'Minimum amount is ' + QUARTERLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'YEARLY',
                then: Yup.number().min(YEARLY?.minimumInstallmentAmount, 'Minimum amount is ' + YEARLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'WEEKLYDAYS',
                then: Yup.number().min(WeeklyDays?.minimumInstallmentAmount, 'Minimum amount is ' + WeeklyDays?.minimumInstallmentAmount),
            }),
            noOfInstallments: Yup.number().default(noOfInstallments).when('frequency', {
                is: 'DAILY',
                // then: Yup.number().min(DAILY?.minimumInstallments, 'Minimum amount is ' + DAILY?.minimumInstallments),
                then: Yup.number().notRequired()
            }).when('frequency', {
                is: 'WEEKLY',
                then: Yup.number().required('Required').min(Weekly?.minimumInstallments, 'Minimum amount is ' + Weekly?.minimumInstallments),
            }).when('frequency', {
                is: 'MONTHLY',
                then: Yup.number().required('Required').min(MONTHLY?.minimumInstallments, 'Minimum amount is ' + MONTHLY?.minimumInstallments),
            }).when('frequency', {
                is: 'QUARTERLY',
                then: Yup.number().required('Required').min(QUARTERLY?.minimumInstallments, 'Minimum amount is ' + QUARTERLY?.minimumInstallments),
            }).when('frequency', {
                is: 'YEARLY',
                then: Yup.number().required('Required').min(YEARLY?.minimumInstallments, 'Minimum amount is ' + YEARLY?.minimumInstallments),
            }).when('frequency', {
                is: 'WEEKLYDAYS',
                then: Yup.number().required('Required').min(WeeklyDays?.minimumInstallments, 'Minimum amount is ' + WeeklyDays?.minimumInstallments),
            }),
            sipDate: Yup.date().nullable().typeError('Invalid date').test('validations', '', function (value, context) {
                const { parent, createError, path } = context;
                const validationData = getDateValidation('SIP', parent?.frequency)

                if (!value) {
                    return createError({ path, message: `SIP ${parent?.frequency === 'DAILY' ? "Start" : ""} Date is Required` });
                }

                // If mandate is active (non-first order)
                if (!parent?.firstOrder) {
                    // For past date
                    if ((dayjs(new Date()).isAfter(value, 'day'))) {
                        return createError({ path, message: 'Past date is not allowed' });
                    }
                    // Earliest allowed date is T+3 working days (Mon–Fri).
                    // Using working-day arithmetic ensures e.g. Thursday + 3 = Tuesday,
                    // not Sunday which would confusingly hit the weekend rule instead.
                    const minWorkingDate = addWorkingDays(dayjs(), 3);
                    if (dayjs(value).isBefore(minWorkingDate, 'day')) {
                        return createError({ path, message: `Minimum start date is ${minWorkingDate.format('DD/MM/YYYY')}` });
                    }
                    // Only working days (Mon–Fri) are allowed for non-first orders.
                    if (dayjs(value).day() === 0 || dayjs(value).day() === 6) {
                        return createError({ path, message: 'Please select a working day (Monday to Friday)' });
                    }
                    const valid = handleDateValidation(validationData?.investmentDates, dayjs(value))
                    if (valid) {
                        return createError({
                            message: `Only ${validationData?.investmentDates.join(', ')} are allowed`,
                            path
                        })
                    }
                    return true
                }

                if (parent?.firstOrder) {
                    const effectiveDate = dayjs().add(31, 'days');
                    if (dayjs(value).isBefore(effectiveDate, 'day')) {
                        return createError({ path, message: `Date should be ${effectiveDate.format('DD/MM/YYYY')} and onwards` });
                    }
                }

                const invalid = handleDateValidation(validationData?.investmentDates, dayjs(value));
                if (invalid) {
                    return createError({
                        path,
                        message: `Only ${validationData?.investmentDates.join(', ')} are allowed`,
                    });
                }

                return true;

                // if (validationData.schemeId) {
                //     if ((dayjs(new Date()).isAfter(value, 'day'))) {
                //         return createError({ path, message: 'Past date is not allowed' });
                //     }

                //     if (validationData?.investmentDates.length === 0) return true

                //     return validationData?.investmentDates?.includes(Number(dayjs(value).format('DD'))) ? true : createError({ path, message: `Date should be in ${validationData?.investmentDates?.join(', ')}` });

                // } else {
                //     return true
                // }
            }),
            endDate: Yup
                .date()
                .nullable()
                .typeError('Invalid Date')
                .test('sip-end-date-check', (value, context) => {
                    const { parent, path, createError } = context

                    if (parent?.frequency === 'DAILY') {
                        if (!value) {
                            return createError({
                                message: 'SIP End Date is Required',
                                path,
                            });
                        }
                        if (parent?.sipDate && value && dayjs(value)?.isBefore(dayjs(parent?.sipDate))) {
                            return createError({
                                message: 'End date should be greater than start date',
                                path
                            })
                        }

                        return true
                    }
                    return true
                }),
            mandates: Yup.string().required('Mandate is required'),
            ...(activeCategory === "New Purchase" ? { folios: Yup.string().required('Folio is Required') } : {}),
        })
    }, [scheme, sipAmount, frequency, noOfInstallments, sipDate])
}


export const LumpsumScheme = ({ folioNumber, lumpsumAmount, activeCategory, scheme, currencySymbol }: { folioNumber: any, lumpsumAmount: any, activeCategory: string, scheme: InvestmentFundType, currencySymbol: string }): any => useMemo(() => {
    const filterValidation = scheme?.validations?.filter((item) => item?.transactionType === 'SUB')?.[0]

    const isAdditionalPurchase = `${activeCategory ?? ""}`.toLowerCase() === "additional purchase"

    return Yup.object().shape({
        // lumpsumAmount: Yup.number().required('Lumpsum Amount is Required').min((filterValidation?.minimumInstallmentAmount || 0), `Minimum amount is ${filterValidation?.minimumInstallmentAmount || 0}`),
        lumpsumAmount: Yup
            .number()
            .required('Lumpsum Amount is Required')
            .test('validations', '', function (value, context) {
                const { parent, createError, path } = context;

                const isNewFolioSelected = `${parent?.folios ?? ""}`?.toLowerCase() === "new folio"

                let minLumpsumAmount = filterValidation?.minimumInstallmentAmount ?? 0

                if (isAdditionalPurchase || (!isAdditionalPurchase && !isNewFolioSelected)) {
                    minLumpsumAmount = filterValidation?.addtionalMinimumPurchaseAmpunt ?? 0;
                }


                return Number(value) >= (minLumpsumAmount || 0) ? true : createError({ path, message: `Minimum amount is ${String.fromCharCode(parseInt(currencySymbol, 16))} ${minLumpsumAmount}` });
            }),
        ...(activeCategory === "New Purchase" ? { folios: Yup.string().required('Folio is Required') } : {}),
    })
}, [lumpsumAmount, activeCategory, scheme])


export const SwpScheme = ({ validation, swpAmount, frequency, noOfInstallments, swpDate, bankAccountNumber, scheme }: { validation: any, swpAmount: any, frequency: any, noOfInstallments: any, swpDate: any, bankAccountNumber: any, scheme: InvestmentFundType }) => {

    const filterValidation = scheme?.validations?.filter((item) => item?.transactionType === 'SWP')?.[0]
    const frequencyArray = scheme?.frequencies?.filter(item => item.frequency && item.tranasactionType === 'SWP')

    const DAILY = scheme.validations?.filter((item) => item?.frequency === 'DAILY' && item?.transactionType === 'SWP')?.[0]
    const Weekly = scheme.validations?.filter((item) => item?.frequency === 'WEEKLY' && item?.transactionType === 'SWP')?.[0]
    const WeeklyDay = scheme.validations?.filter((item) => item?.frequency === 'WEEKLYDAYS' && item?.transactionType === 'SWP')?.[0]
    const MONTHLY = scheme.validations?.filter((item) => item?.frequency === 'MONTHLY' && item?.transactionType === 'SWP')?.[0]
    const QUARTERLY = scheme.validations?.filter((item) => item?.frequency === 'QUARTERLY' && item?.transactionType === 'SWP')?.[0]
    const YEARLY = scheme.validations?.filter((item) => item?.frequency === 'YEARLY' && item?.transactionType === 'SWP')?.[0]

    const commonValidation = (frequency: string) => scheme?.validations?.filter((item) => (item?.frequency === frequency && item.transactionType === 'SWP'))?.[0]

    return useMemo(() => {
        return Yup.object().shape({
            frequency: Yup.string().required('Please select frequency').default(swpAmount),
            swpAmount: Yup.number().required('Required').when('frequency', {
                is: 'DAILY',
                then: Yup.number().min(DAILY?.minimumInstallmentAmount, 'Minimum amount is ' + DAILY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'WEEKLY',
                then: Yup.number().min(Weekly?.minimumInstallmentAmount, 'Minimum amount is ' + Weekly?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'MONTHLY',
                then: Yup.number().min(MONTHLY?.minimumInstallmentAmount, 'Minimum amount is ' + MONTHLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'QUARTERLY',
                then: Yup.number().min(QUARTERLY?.minimumInstallmentAmount, 'Minimum amount is ' + QUARTERLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'YEARLY',
                then: Yup.number().min(YEARLY?.minimumInstallmentAmount, 'Minimum amount is ' + YEARLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'WEEKLYDAYS',
                then: Yup.number().min(WeeklyDay?.minimumInstallmentAmount, 'Minimum amount is ' + WeeklyDay?.minimumInstallmentAmount),
            }),
            noOfInstallments: Yup.number().required('Required').default(noOfInstallments).when('frequency', {
                is: 'DAILY',
                then: Yup.number().min(DAILY?.minimumInstallments, 'Minimum amount is ' + DAILY?.minimumInstallments),
            }).when('frequency', {
                is: 'WEEKLY',
                then: Yup.number().min(Weekly?.minimumInstallments, 'Minimum amount is ' + Weekly?.minimumInstallments),
            }).when('frequency', {
                is: 'MONTHLY',
                then: Yup.number().min(MONTHLY?.minimumInstallments, 'Minimum amount is ' + MONTHLY?.minimumInstallments),
            }).when('frequency', {
                is: 'QUARTERLY',
                then: Yup.number().min(QUARTERLY?.minimumInstallments, 'Minimum amount is ' + QUARTERLY?.minimumInstallments),
            }).when('frequency', {
                is: 'YEARLY',
                then: Yup.number().min(YEARLY?.minimumInstallments, 'Minimum amount is ' + YEARLY?.minimumInstallments),
            }).when('frequency', {
                is: 'WEEKLYDAYS',
                then: Yup.number().min(WeeklyDay?.minimumInstallments, 'Minimum amount is ' + WeeklyDay?.minimumInstallments),
            }),
            mandates: Yup.string().required('Please select Mandate').default(bankAccountNumber),
            // need to change stpdate validation
            swpDate: Yup.date().typeError('Invalid date').min(dayjs().add(-1, 'day'), 'Past date are not allowed').required('SWP Date is Required')
                .test(
                    'frequency', (value, context) => {
                        const { path, createError } = context
                        const { frequency } = context.parent
                        // For past date
                        if ((dayjs(new Date()).isAfter(value, 'day'))) {
                            return createError({ path, message: 'Past date is not allowed' });
                        }
                        // For T+1 date
                        if ((dayjs(new Date()).add(1, 'day').isAfter(value, 'day'))) {
                            return createError({ path, message: 'Date should be T+1' });
                        }
                        const validation = commonValidation(frequency)
                        const valid = handlePickDate(validation?.investmentDates, dayjs(value))
                        if (valid) {
                            return createError({
                                message: `Only ${validation?.investmentDates.join(', ')} are allowed`,
                                path
                            })
                        }
                        return true
                    }
                ),
        })
    }, [validation, swpAmount, frequency, noOfInstallments, swpDate, bankAccountNumber, scheme])
}


export const StpScheme = ({ stpAmount, frequency, noOfInstallments, stpDate, scheme }: { stpAmount: any, frequency: any, noOfInstallments: any, stpDate: any, scheme: InvestmentFundType }) => {
    const filterValidation = scheme?.validations?.filter((item) => item?.transactionType === 'STP')?.[0]
    const frequencyArray = scheme?.frequencies?.filter(item => item.frequency && item.tranasactionType === 'STP')

    const DAILY = scheme?.validations?.filter((item) => item?.frequency === 'DAILY' && item?.transactionType === 'STP')?.[0]
    const Weekly = scheme?.validations?.filter((item) => item?.frequency === 'WEEKLY' && item?.transactionType === 'STP')?.[0]
    const WeeklyDays = scheme?.validations?.filter((item) => item?.frequency === 'WEEKLYDAYS' && item?.transactionType === 'STP')?.[0]
    const MONTHLY = scheme?.validations?.filter((item) => item?.frequency === 'MONTHLY' && item?.transactionType === 'STP')?.[0]
    const QUARTERLY = scheme?.validations?.filter((item) => item?.frequency === 'QUARTERLY' && item?.transactionType === 'STP')?.[0]
    const YEARLY = scheme?.validations?.filter((item) => item?.frequency === 'YEARLY' && item?.transactionType === 'STP')?.[0]

    const commonValidation = (frequency: string) => scheme?.validations?.filter((item) => (item?.frequency === frequency && item.transactionType === 'STP'))?.[0]

    return useMemo(() => {
        return Yup.object().shape({
            frequency: Yup.string().required('Please select frequency'),
            stpAmount: Yup.number().required('Required').when('frequency', {
                is: 'DAILY',
                then: Yup.number().min(DAILY?.minimumInstallmentAmount, 'Minimum amount is ' + DAILY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'WEEKLY',
                then: Yup.number().min(Weekly?.minimumInstallmentAmount, 'Minimum amount is ' + Weekly?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'MONTHLY',
                then: Yup.number().min(MONTHLY?.minimumInstallmentAmount, 'Minimum amount is ' + MONTHLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'QUARTERLY',
                then: Yup.number().min(QUARTERLY?.minimumInstallmentAmount, 'Minimum amount is ' + QUARTERLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'YEARLY',
                then: Yup.number().min(YEARLY?.minimumInstallmentAmount, 'Minimum amount is ' + YEARLY?.minimumInstallmentAmount),
            }).when('frequency', {
                is: 'WEEKLYDAYS',
                then: Yup.number().min(WeeklyDays?.minimumInstallmentAmount, 'Minimum amount is ' + WeeklyDays?.minimumInstallmentAmount),
            }),
            noOfInstallments: Yup.number()
                .when('frequency', {
                    is: 'DAILY',
                    then: Yup.number().notRequired(),
                }).when('frequency', {
                    is: 'WEEKLY',
                    then: Yup.number().required('Installments is Required').min(Weekly?.minimumInstallments, 'Minimum amount is ' + Weekly?.minimumInstallments),
                }).when('frequency', {
                    is: 'MONTHLY',
                    then: Yup.number().required('Installments is Required').min(MONTHLY?.minimumInstallments, 'Minimum amount is ' + MONTHLY?.minimumInstallments),
                }).when('frequency', {
                    is: 'QUARTERLY',
                    then: Yup.number().required('Installments is Required').min(QUARTERLY?.minimumInstallments, 'Minimum amount is ' + QUARTERLY?.minimumInstallments),
                }).when('frequency', {
                    is: 'YEARLY',
                    then: Yup.number().required('Installments is Required').min(YEARLY?.minimumInstallments, 'Minimum amount is ' + YEARLY?.minimumInstallments),
                }).when('frequency', {
                    is: 'WEEKLYDAYS',
                    then: Yup.number().required('Installments is Required').min(WeeklyDays?.minimumInstallments, 'Minimum amount is ' + WeeklyDays?.minimumInstallments),
                }),

            // need to change stpdate validation
            stpDate: Yup.date().typeError('Invalid date').min(dayjs().add(-1, 'day'), 'Past date are not allowed').required('STP Date is Required')
                .test(
                    'frequency', '', (value, context) => {
                        const { path, createError } = context
                        const { frequency } = context.parent

                        // For past date
                        if ((dayjs(new Date()).isAfter(value, 'day'))) {
                            return createError({ path, message: 'Past date is not allowed' });
                        }
                        // For T+1 date
                        if ((dayjs(new Date()).add(1, 'day').isAfter(value, 'day'))) {
                            return createError({ path, message: 'Date should be T+1' });
                        }
                        const validation = commonValidation(frequency)
                        const valid = handleStpDateValidation({
                            ArrayOfDate: validation?.investmentDates,
                            date: dayjs(value),
                            weekDaysAllowed: context?.parent?.weeklyDaysValidation?.STP_WEEKDAYS ?? [],
                            frequency: context?.parent?.frequency,
                        })
                        if (valid) {
                            return createError({
                                message: `Only ${context?.parent?.frequency === "WEEKLY" ? (context?.parent?.weeklyDaysValidation?.STP_WEEKDAYS ?? []).join(', ') : validation?.investmentDates.join(', ')} are allowed`,
                                path
                            })
                        }
                        return true
                    }
                ),
            stpEndDate: Yup.date()
                .typeError('Invalid date')
                .when('frequency', {
                    is: 'DAILY',
                    then: Yup.date().required('STP End date is required').test((value, context) => {
                        const { parent, path, createError } = context
                        if (parent?.stpDate && dayjs(value)?.isBefore(dayjs(parent?.stpDate))) {
                            return createError({
                                message: 'End date should be greater than start date',
                                path
                            })
                        }
                        return true
                    }),
                    otherwise: Yup.date().notRequired()
                })

        })
    }, [stpAmount, frequency, noOfInstallments, stpDate, scheme])
}


export const SwitchScheme = ({ validation, switchValue, switchType, accountType, scheme, freeUnits }: { validation: any, switchValue: any, switchType: any, accountType: any, scheme: InvestmentFundType, freeUnits: number }) => {
    const { currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    const filterValidation = scheme?.validations?.filter((item) => item?.transactionType === 'RED')?.[0]

    return Yup.object().shape({
        switchType: Yup
            .string()
            .required('Required'),
        switchValue: Yup
            .mixed()
            .test("switch-type-validation", (value, context) => {
                const { path, createError, parent } = context

                if (parent?.switchType === "A") {
                    if (!`${value ?? ""}`.length) {
                        return createError({ path, message: 'Amount is required' })
                    }
                    if (Number.isNaN(Number(value))) {
                        return createError({ path, message: 'Invalid number' })
                    }
                    if (Number(value) < Number(filterValidation?.minimumInstallmentAmount ?? 0)) {
                        return createError({
                            path, message: `Minimum amount should be ${String.fromCharCode(parseInt(currencySymbol, 16))} ${Number(filterValidation?.minimumInstallmentAmount ?? 0)}`
                        })
                    }

                    return true
                } else {
                    if (!`${value ?? ""}`.length) {
                        return createError({ path, message: 'Units is required' })
                    }
                    if (Number.isNaN(Number(value))) {
                        return createError({ path, message: 'Invalid number' })
                    }
                    const decimalPart = `${value ?? ""}`.split('.')[1]
                    if (decimalPart && decimalPart.length > 3) {
                        return createError({ path, message: 'Maximum 3 digits are allowed after decimal point' })
                    }
                    if (Number(value) > Number(freeUnits ?? 0)) {
                        return createError({ path, message: `Maximum ${freeUnits ?? 0} units are available` })
                    }

                    return true
                }
            }),
        // switchValue: Yup.number().required('Required').default(switchValue).when('switchType', {
        //     is: 'A',
        //     then: Yup.number().min(filterValidation?.minimumInstallmentAmount || 0, `Minimum ${String.fromCharCode(parseInt(currencySymbol, 16))} ${filterValidation?.minimumInstallmentAmount}`).required('Required'),
        // }).when('switchType', {
        //     is: 'U',
        //     then: Yup.number().max(freeUnits || 0, `Maximum ${freeUnits} units are available`).required('Required'),
        // }),
    })
}

export const ReedemScheme = ({ validation, redeemValue, switchType, accountType, scheme, freeUnits = 0 }: { validation: any, redeemValue: any, switchType: any, accountType: any, scheme: InvestmentFundType, freeUnits: number }) => {
    const { currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    const filterValidation = scheme?.validations?.filter((item) => item?.transactionType === 'RED')?.[0]
    return Yup.object().shape({
        switchType: Yup.string().required('Required'),
        redeemValue: Yup.number().required('Required').when('switchType', {
            is: 'A',
            then: Yup.number().min(filterValidation?.minimumInstallmentAmount || 0, `Minimum ${String.fromCharCode(parseInt(currencySymbol, 16))} ${filterValidation?.minimumInstallmentAmount || 0}`),
        }).when('switchType', {
            is: 'U',
            // then: Yup.number().min(filterValidation?.minimumInstallmentUnits || 0, `Minimum ${filterValidation?.minimumInstallmentUnits || 0}`),
            then: Yup.number()
                .test('decimal-digits', '', function (value) {
                    const { path, createError } = this
                    const decimalPart = `${value ?? ""}`.split('.')[1]
                    if (decimalPart && decimalPart.length > 3) {
                        return createError({ path, message: 'Maximum 3 digits are allowed after decimal point' })
                    }
                    return true
                })
                .max(freeUnits || 0, `Maximum ${freeUnits || 0} units are available`),
        }),
        accountType: Yup.string().required('Required'),
        entireBalance: Yup.boolean()

    })
}
